import { useMemo, useRef, useState, useEffect } from "react";
import { MagnifyingGlass, CaretDown, X } from "@phosphor-icons/react";
import { regSort } from "@/lib/csv";

/**
 * VehicleSearchSelect — searchable dropdown filtered by registration number.
 *
 * Props:
 *   vehicles      — array of vehicle objects (must have id, reg_number, make, model)
 *   value         — currently-selected vehicle id (or "")
 *   onChange(id)  — called with the new id
 *   label         — field label rendered above the input
 *   placeholder   — prompt text when nothing is selected (default "Search by registration number…")
 *   filter        — optional (vehicle) => boolean predicate to narrow options
 *   allowClear    — shows an × button to reset selection (default false)
 *   testid        — data-testid prefix for the input + options
 *   className     — extra classes on the wrapper
 *   disabled      — when true, input is disabled
 */
export default function VehicleSearchSelect({
    vehicles = [],
    value = "",
    onChange,
    label,
    placeholder = "Search by registration number…",
    filter,
    allowClear = false,
    testid = "vehicle-search",
    className = "",
    disabled = false,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const inputRef = useRef(null);
    const wrapRef = useRef(null);

    const selected = useMemo(
        () => vehicles.find((v) => v.id === value) || null,
        [vehicles, value]
    );

    // Keep the visible query mirroring the selected vehicle when not actively searching
    useEffect(() => {
        if (!open) setQuery(selected ? selected.reg_number : "");
    }, [selected, open]);

    // Close on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const options = useMemo(() => {
        const q = query.trim().toLowerCase();
        const base = (filter ? vehicles.filter(filter) : vehicles).slice().sort(regSort);
        if (!q) return base;
        return base.filter((v) =>
            (v.reg_number || "").toLowerCase().includes(q) ||
            (v.make || "").toLowerCase().includes(q) ||
            (v.model || "").toLowerCase().includes(q)
        );
    }, [vehicles, query, filter]);

    const pick = (v) => {
        onChange?.(v.id);
        setQuery(v.reg_number);
        setOpen(false);
    };

    const clear = () => {
        onChange?.("");
        setQuery("");
        inputRef.current?.focus();
    };

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            {label && <label className="label-mono block mb-1.5">{label}</label>}
            <div className={`flex items-center gap-2 px-2.5 py-2 border rounded-md bg-white text-sm
                ${disabled ? "bg-slate-50 cursor-not-allowed" : "border-fleet-border focus-within:border-fleet-ink"}`}>
                <MagnifyingGlass size={14} className="text-fleet-inkMuted shrink-0" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="flex-1 bg-transparent outline-none placeholder:text-fleet-inkMuted disabled:cursor-not-allowed"
                    data-testid={`${testid}-input`}
                />
                {selected && allowClear && !disabled && (
                    <button
                        type="button"
                        onClick={clear}
                        className="text-fleet-inkSoft hover:text-fleet-critical"
                        title="Clear"
                        data-testid={`${testid}-clear`}
                    >
                        <X size={14} />
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => !disabled && setOpen((o) => !o)}
                    className="text-fleet-inkSoft hover:text-fleet-ink"
                    tabIndex={-1}
                >
                    <CaretDown size={12} />
                </button>
            </div>

            {open && !disabled && (
                <div
                    className="absolute z-40 left-0 right-0 mt-1 bg-white border border-fleet-border rounded-md shadow-lg max-h-72 overflow-y-auto"
                    data-testid={`${testid}-dropdown`}
                >
                    {options.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-fleet-inkSoft">
                            {vehicles.length === 0 ? "No vehicles available." : "No matches for that search."}
                        </div>
                    ) : (
                        options.map((v) => (
                            <button
                                key={v.id}
                                type="button"
                                onMouseDown={() => pick(v)}
                                className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between ${v.id === value ? "bg-slate-100" : ""}`}
                                data-testid={`${testid}-option-${v.id}`}
                            >
                                <span className="font-mono font-medium text-sm">{v.reg_number}</span>
                                <span className="text-xs text-fleet-inkSoft truncate ml-3">
                                    {[v.make, v.model].filter(Boolean).join(" ")}
                                    {v.odometer != null && <span className="ml-2 text-fleet-inkMuted">{(v.odometer||0).toLocaleString()} km</span>}
                                </span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
